import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { 
  Search, 
  ChevronUp, 
  ChevronDown, 
  Save, 
  FolderOpen,
  Download,
  Upload,
  Undo,
  Redo,
  Copy,
  Scissors,
  ClipboardPaste
} from 'lucide-react';
import { useToast } from '@os/hooks/use-toast';
import { DraggableFileDialog } from '../DraggableFileDialog';
import { useUserVFS } from '@os/hooks/useUserVFS';

interface HexEditorProps {
  filePath?: string;
  initialData?: string;
  onClose?: () => void;
}

export const HexEditor: React.FC<HexEditorProps> = ({ 
  filePath: initialFilePath, 
  initialData = '', 
  onClose 
}) => {
  const [data, setData] = useState<Uint8Array>(new Uint8Array(0));
  const [filePath, setFilePath] = useState<string>(initialFilePath || '');
  const [bytesPerLine, setBytesPerLine] = useState<number>(16);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(0);
  const [selectedByte, setSelectedByte] = useState<number>(-1);
  const [selectionStart, setSelectionStart] = useState<number>(-1);
  const [selectionEnd, setSelectionEnd] = useState<number>(-1);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);
  const [showFileBrowser, setShowFileBrowser] = useState<boolean>(false);
  const [history, setHistory] = useState<Uint8Array[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [clipboard, setClipboard] = useState<Uint8Array>(new Uint8Array(0));
  const [displayMode, setDisplayMode] = useState<'hex' | 'text'>('hex');
  const [isModified, setIsModified] = useState<boolean>(false);
  
  const hexViewRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { getFile, updateFile } = useUserVFS();

  // Load initial data
  useEffect(() => {
    if (initialData) {
      // Convert string to bytes
      const encoder = new TextEncoder();
      const bytes = encoder.encode(initialData);
      setData(bytes);
      addToHistory(bytes);
    } else if (initialFilePath) {
      loadFileFromVFS(initialFilePath);
    }
  }, [initialFilePath, initialData]);

  const loadFileFromVFS = async (path: string) => {
    try {
      const file = getFile(path);
      if (file && file.content) {
        // Convert file content to bytes
        const encoder = new TextEncoder();
        let bytes: Uint8Array;
        
        // Try to detect if content is base64 encoded binary data
        try {
          if (file.content.match(/^[A-Za-z0-9+/]*={0,2}$/)) {
            // Likely base64
            const binaryString = atob(file.content);
            bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
          } else {
            // Regular text
            bytes = encoder.encode(file.content);
          }
        } catch {
          // Fallback to text encoding
          bytes = encoder.encode(file.content);
        }
        
        setData(bytes);
        setFilePath(path);
        addToHistory(bytes);
        setIsModified(false);
        
        toast({
          title: "File Loaded",
          description: `Loaded ${bytes.length} bytes from ${path}`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load file",
        variant: "destructive",
      });
    }
  };

  const addToHistory = (newData: Uint8Array) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(new Uint8Array(newData));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setData(new Uint8Array(history[historyIndex - 1]));
      setIsModified(true);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setData(new Uint8Array(history[historyIndex + 1]));
      setIsModified(true);
    }
  };

  const handleByteChange = (index: number, value: number) => {
    if (value >= 0 && value <= 255) {
      const newData = new Uint8Array(data);
      newData[index] = value;
      setData(newData);
      addToHistory(newData);
      setIsModified(true);
    }
  };

  const handleSearch = () => {
    if (!searchTerm) return;
    
    const results: number[] = [];
    const searchBytes = new TextEncoder().encode(searchTerm.toLowerCase());
    
    for (let i = 0; i <= data.length - searchBytes.length; i++) {
      let match = true;
      for (let j = 0; j < searchBytes.length; j++) {
        if (data[i + j] !== searchBytes[j]) {
          match = false;
          break;
        }
      }
      if (match) {
        results.push(i);
      }
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
    
    if (results.length > 0) {
      scrollToByte(results[0]);
      toast({
        title: "Search Results",
        description: `Found ${results.length} matches`,
      });
    } else {
      toast({
        title: "Search Results",
        description: "No matches found",
        variant: "destructive",
      });
    }
  };

  const scrollToByte = (byteIndex: number) => {
    const lineIndex = Math.floor(byteIndex / bytesPerLine);
    const element = document.getElementById(`hex-line-${lineIndex}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setSelectedByte(byteIndex);
  };

  const navigateSearch = (direction: 'next' | 'prev') => {
    if (searchResults.length === 0) return;
    
    let newIndex = currentSearchIndex;
    if (direction === 'next') {
      newIndex = (currentSearchIndex + 1) % searchResults.length;
    } else {
      newIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    }
    
    setCurrentSearchIndex(newIndex);
    scrollToByte(searchResults[newIndex]);
  };

  const saveFile = async () => {
    if (!filePath) {
      toast({
        title: "Error",
        description: "No file path specified",
        variant: "destructive",
      });
      return;
    }

    try {
      // Convert bytes back to string (try to detect if it was binary)
      let content: string;
      
      // Check if data contains non-printable characters (likely binary)
      const hasBinary = Array.from(data).some(byte => byte < 32 && byte !== 9 && byte !== 10 && byte !== 13);
      
      if (hasBinary) {
        // Convert to base64 for binary data
        const binaryString = String.fromCharCode(...data);
        content = btoa(binaryString);
      } else {
        // Convert to UTF-8 string for text data
        const decoder = new TextDecoder('utf-8', { fatal: false });
        content = decoder.decode(data);
      }
      
      const success = await updateFile(filePath, content);
      if (success) {
        setIsModified(false);
        toast({
          title: "File Saved",
          description: `Saved ${data.length} bytes to ${filePath}`,
        });
      } else {
        throw new Error('Failed to save file');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save file",
        variant: "destructive",
      });
    }
  };

  const copySelection = () => {
    if (selectionStart >= 0 && selectionEnd >= 0) {
      const start = Math.min(selectionStart, selectionEnd);
      const end = Math.max(selectionStart, selectionEnd);
      const selectedData = data.slice(start, end + 1);
      setClipboard(selectedData);
      
      toast({
        title: "Copied",
        description: `Copied ${selectedData.length} bytes to clipboard`,
      });
    }
  };

  const cutSelection = () => {
    if (selectionStart >= 0 && selectionEnd >= 0) {
      const start = Math.min(selectionStart, selectionEnd);
      const end = Math.max(selectionStart, selectionEnd);
      const selectedData = data.slice(start, end + 1);
      setClipboard(selectedData);
      
      // Remove selected bytes
      const newData = new Uint8Array(data.length - (end - start + 1));
      newData.set(data.slice(0, start));
      newData.set(data.slice(end + 1), start);
      
      setData(newData);
      addToHistory(newData);
      setIsModified(true);
      setSelectionStart(-1);
      setSelectionEnd(-1);
      
      toast({
        title: "Cut",
        description: `Cut ${selectedData.length} bytes to clipboard`,
      });
    }
  };

  const pasteClipboard = () => {
    if (clipboard.length === 0) return;
    
    const insertPoint = selectedByte >= 0 ? selectedByte : data.length;
    const newData = new Uint8Array(data.length + clipboard.length);
    
    newData.set(data.slice(0, insertPoint));
    newData.set(clipboard, insertPoint);
    newData.set(data.slice(insertPoint), insertPoint + clipboard.length);
    
    setData(newData);
    addToHistory(newData);
    setIsModified(true);
    
    toast({
      title: "Pasted",
      description: `Pasted ${clipboard.length} bytes`,
    });
  };

  const handleMouseDown = (index: number) => {
    setIsSelecting(true);
    setSelectionStart(index);
    setSelectionEnd(index);
    setSelectedByte(index);
  };

  const handleMouseEnter = (index: number) => {
    if (isSelecting) {
      setSelectionEnd(index);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
  };

  const isByteSelected = (index: number) => {
    if (selectionStart < 0 || selectionEnd < 0) return false;
    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);
    return index >= start && index <= end;
  };

  const isSearchMatch = (index: number) => {
    return searchResults.includes(index);
  };

  const formatByte = (byte: number, mode: 'hex' | 'text') => {
    if (mode === 'hex') {
      return byte.toString(16).padStart(2, '0').toUpperCase();
    } else {
      // Show printable ASCII or dot for non-printable
      return byte >= 32 && byte <= 126 ? String.fromCharCode(byte) : '.';
    }
  };

  const renderHexView = () => {
    const lines: React.ReactElement[] = [];
    
    for (let i = 0; i < data.length; i += bytesPerLine) {
      const lineBytes = data.slice(i, Math.min(i + bytesPerLine, data.length));
      const lineNumber = i.toString(16).padStart(8, '0').toUpperCase();
      
      lines.push(
        <div key={i} id={`hex-line-${Math.floor(i / bytesPerLine)}`} className="flex font-mono text-sm">
          {/* Line number */}
          <div className="w-20 text-muted-foreground mr-4">
            {lineNumber}
          </div>
          
          {/* Hex bytes */}
          <div className="flex-1 flex flex-wrap gap-1">
            {Array.from({ length: bytesPerLine }, (_, j) => {
              const byteIndex = i + j;
              const byte = lineBytes[j];
              
              if (byte === undefined) {
                return <span key={j} className="w-6"></span>;
              }
              
              const isSelected = isByteSelected(byteIndex);
              const isMatch = isSearchMatch(byteIndex);
              const isCurrent = selectedByte === byteIndex;
              
              return (
                <span
                  key={j}
                  className={`w-6 h-5 flex items-center justify-center cursor-pointer border rounded ${
                    isCurrent ? 'border-primary bg-primary/20' :
                    isSelected ? 'bg-accent text-accent-foreground' :
                    isMatch ? 'bg-yellow-200 dark:bg-yellow-900' :
                    'border-transparent hover:border-muted-foreground'
                  }`}
                  onMouseDown={() => handleMouseDown(byteIndex)}
                  onMouseEnter={() => handleMouseEnter(byteIndex)}
                  onMouseUp={handleMouseUp}
                  onDoubleClick={() => {
                    const newValue = prompt(`Edit byte at ${byteIndex.toString(16)}:`, byte.toString(16));
                    if (newValue !== null) {
                      const parsed = parseInt(newValue, 16);
                      if (!isNaN(parsed)) {
                        handleByteChange(byteIndex, parsed);
                      }
                    }
                  }}
                >
                  {formatByte(byte, 'hex')}
                </span>
              );
            })}
          </div>
          
          {/* ASCII representation */}
          <div className="w-4 ml-4"></div>
          <div className="flex">
            {Array.from({ length: bytesPerLine }, (_, j) => {
              const byteIndex = i + j;
              const byte = lineBytes[j];
              
              if (byte === undefined) {
                return <span key={j} className="w-3"></span>;
              }
              
              const isSelected = isByteSelected(byteIndex);
              const isMatch = isSearchMatch(byteIndex);
              const isCurrent = selectedByte === byteIndex;
              
              return (
                <span
                  key={j}
                  className={`w-3 h-5 flex items-center justify-center cursor-pointer ${
                    isCurrent ? 'bg-primary/20' :
                    isSelected ? 'bg-accent text-accent-foreground' :
                    isMatch ? 'bg-yellow-200 dark:bg-yellow-900' :
                    'hover:bg-muted'
                  }`}
                  onMouseDown={() => handleMouseDown(byteIndex)}
                  onMouseEnter={() => handleMouseEnter(byteIndex)}
                  onMouseUp={handleMouseUp}
                >
                  {formatByte(byte, 'text')}
                </span>
              );
            })}
          </div>
        </div>
      );
    }
    
    return lines;
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b bg-muted/50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFileBrowser(true)}
        >
          <FolderOpen className="w-4 h-4 mr-1" />
          Open
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={saveFile}
          disabled={!isModified}
        >
          <Save className="w-4 h-4 mr-1" />
          Save
        </Button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <Button
          variant="outline"
          size="sm"
          onClick={undo}
          disabled={historyIndex <= 0}
        >
          <Undo className="w-4 h-4" />
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={redo}
          disabled={historyIndex >= history.length - 1}
        >
          <Redo className="w-4 h-4" />
        </Button>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <Button
          variant="outline"
          size="sm"
          onClick={copySelection}
          disabled={selectionStart < 0 || selectionEnd < 0}
        >
          <Copy className="w-4 h-4" />
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={cutSelection}
          disabled={selectionStart < 0 || selectionEnd < 0}
        >
          <Scissors className="w-4 h-4" />
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={pasteClipboard}
          disabled={clipboard.length === 0}
        >
          <ClipboardPaste className="w-4 h-4" />
        </Button>
        
        <div className="flex-1" />
        
        {/* Bytes per line selector */}
        <Select value={bytesPerLine.toString()} onValueChange={(value) => setBytesPerLine(parseInt(value))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="8">8 bytes</SelectItem>
            <SelectItem value="16">16 bytes</SelectItem>
            <SelectItem value="32">32 bytes</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Search bar */}
      <div className="flex items-center gap-2 p-2 border-b">
        <Search className="w-4 h-4 text-muted-foreground" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search for text..."
          className="flex-1"
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button variant="outline" size="sm" onClick={handleSearch}>
          Search
        </Button>
        {searchResults.length > 0 && (
          <>
            <span className="text-sm text-muted-foreground">
              {currentSearchIndex + 1} of {searchResults.length}
            </span>
            <Button variant="outline" size="sm" onClick={() => navigateSearch('prev')}>
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigateSearch('next')}>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
      
      {/* Status bar */}
      <div className="flex items-center gap-4 px-4 py-2 border-b bg-muted/30 text-sm">
        <span>File: {filePath || 'Untitled'}</span>
        <span>Size: {data.length} bytes</span>
        {selectedByte >= 0 && (
          <>
            <span>Position: 0x{selectedByte.toString(16).toUpperCase()}</span>
            <span>Value: 0x{data[selectedByte]?.toString(16).padStart(2, '0').toUpperCase()}</span>
            <span>Decimal: {data[selectedByte]}</span>
          </>
        )}
        {isModified && <span className="text-amber-600">Modified</span>}
      </div>
      
      {/* Hex view */}
      <div 
        ref={hexViewRef}
        className="flex-1 overflow-auto p-4 bg-background"
        onMouseUp={handleMouseUp}
      >
        {data.length > 0 ? (
          renderHexView()
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <div className="text-lg mb-2">No data loaded</div>
              <div className="text-sm">Open a file to start editing</div>
            </div>
          </div>
        )}
      </div>
      
      {/* File Browser */}
      <DraggableFileDialog
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        onFileSelect={(file) => {
          // Load file content directly from FileSystemItem
          if (file.content) {
            const encoder = new TextEncoder();
            const bytes = encoder.encode(file.content);
            setData(bytes);
            setFilePath(file.path);
            addToHistory(bytes);
            setIsModified(false);
            
            toast({
              title: "File Loaded",
              description: `Loaded ${bytes.length} bytes from ${file.path}`,
            });
          } else {
            toast({
              title: "Error",
              description: "File has no content to load",
              variant: "destructive",
            });
          }
          setShowFileBrowser(false);
        }}
        mode="open"
        title="Open File in Hex Editor"
      />
    </div>
  );
};